/** Helpers for Tiptap / ProseMirror JSON documents (comments, chat messages, descriptions, docs). */

export interface RichNode {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  content?: RichNode[];
}

const BLOCKS = new Set([
  'paragraph',
  'heading',
  'listItem',
  'taskItem',
  'codeBlock',
  'blockquote',
  'horizontalRule',
  'tableRow',
]);

function isNode(value: unknown): value is RichNode {
  return typeof value === 'object' && value !== null && typeof (value as RichNode).type === 'string';
}

/** Depth-first walk over every node. */
export function walkRichText(doc: unknown, visit: (node: RichNode) => void): void {
  if (!isNode(doc)) return;
  visit(doc);
  for (const child of doc.content ?? []) walkRichText(child, visit);
}

/** Plain text for search indexes, notifications and previews. */
export function richTextToPlain(doc: unknown): string {
  const parts: string[] = [];
  const render = (node: RichNode): void => {
    if (node.type === 'text') parts.push(node.text ?? '');
    else if (node.type === 'mention') parts.push(`@${String(node.attrs?.label ?? node.attrs?.id ?? '')}`);
    else if (node.type === 'taskMention') parts.push(`#${String(node.attrs?.label ?? node.attrs?.id ?? '')}`);
    else if (node.type === 'hardBreak') parts.push('\n');
    for (const child of node.content ?? []) if (isNode(child)) render(child);
    if (BLOCKS.has(node.type)) parts.push('\n');
  };
  if (isNode(doc)) render(doc);
  return parts
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Ids of mentioned users. `@channel` style mentions use the ids `channel` or `everyone`. */
export function extractMentions(doc: unknown): string[] {
  const ids = new Set<string>();
  walkRichText(doc, (node) => {
    if (node.type === 'mention' && typeof node.attrs?.id === 'string') ids.add(node.attrs.id);
  });
  return [...ids];
}

const DOC_LINK = /[/]docs[/](doc_[A-Za-z0-9_-]+)(?:[/?#]|$)/;

/** Ids of Regent docs the text points at: doc mentions and links to a doc's page in the app. */
export function linkedDocIds(doc: unknown): string[] {
  const ids = new Set<string>();
  walkRichText(doc, (node) => {
    if (node.type === 'docMention' && typeof node.attrs?.docId === 'string') ids.add(node.attrs.docId);
    for (const mark of node.marks ?? []) {
      if (mark.type !== 'link' || typeof mark.attrs?.href !== 'string') continue;
      const match = DOC_LINK.exec(mark.attrs.href);
      if (match?.[1]) ids.add(match[1]);
    }
  });
  return [...ids];
}

export function isRichTextEmpty(doc: unknown): boolean {
  if (!isNode(doc)) return true;
  let has = false;
  walkRichText(doc, (node) => {
    if (
      (node.type === 'text' && node.text?.trim()) ||
      node.type === 'mention' ||
      node.type === 'taskMention' ||
      node.type === 'image'
    )
      has = true;
  });
  return !has;
}

/** Wraps plain text into a document, one paragraph per line. */
export function plainToRichText(text: string): RichNode {
  return {
    type: 'doc',
    content: text.split('\n').map((line) => ({
      type: 'paragraph',
      ...(line ? { content: [{ type: 'text', text: line }] } : {}),
    })),
  };
}
